import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { alertAPI, basesAPI, listingAPI, reviewAPI, walkthroughAPI } from '../lib/api';
import {
  isFromAdminListings,
  ADMIN_LISTINGS_RETURN_URL,
} from '../lib/adminListingsNavigation';
import BookingInquiryContextBanner from '../components/BookingInquiryContextBanner';
import {
  parseInquiryIdFromSearchParams,
  pickSafeInquiryContextForBanner,
  resolveInquiryFromList,
} from '../lib/bookingInquiryContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import ShareButton from '../components/ShareButton';
import FavoriteButton from '../components/FavoriteButton';
import BookingCalendar from '../components/BookingCalendar';
import {
  getOwnerConfirmationLabel,
  formatCurrency,
  formatDate,
  getBaseRent,
  getMonthlyPriceLabelKey,
  getMonthlyTotal,
  hasValue,
  translateTemplate,
} from '../lib/publicListingTruth';
import { findSampleListingById, sampleReviewsByListingId } from '../lib/samplePreviewData';
import { getSamplePreviewEnabled, subscribeToSamplePreviewMode } from '../lib/samplePreviewMode';
import { AREA_DISTANCE_HELPER_COPY, buildBaseLookup, findNearestListedBase, getListingAreaDistanceLine, resolveBaseForLabel } from '../lib/areaDistance';
import AreaIntelligenceModule from '../components/areaIntelligence/AreaIntelligenceModule';
import { getUnitPreference } from '../lib/unitPreference';
import { listingAnalyticsProps, trackEvent } from '../lib/analytics';
import { resolveListingPhotoEntries } from '../lib/listingPhotoDisplay';
import ListingPhotoPending from '../components/ListingPhotoPending';
import {
  ArrowLeft,
  Bath,
  Bed,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Euro,
  ExternalLink,
  Home,
  Info,
  Loader2,
  MapPin,
  Maximize2,
  ParkingCircle,
  Shield,
  Star,
  Trash2,
  User,
  Video,
  View,
  Wifi,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

function slugifyListingEnumValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[+]/g, '_plus_')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function translateListingEnum(t, field, value) {
  if (!hasValue(value)) return '—';
  const slug = slugifyListingEnumValue(value);
  if (!slug) return value;
  const key = `property.enum.${field}.${slug}`;
  const translated = t(key, '');
  return translated || value;
}

function CostRow({ icon, label, description, value, emphasize = false }) {
  return (
    <div className="flex justify-between items-center gap-4 py-3 border-b border-slate-200 last:border-b-0">
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <div className="min-w-0">
          <div className="text-slate-700">{label}</div>
          {description && <div className="text-xs text-slate-500">{description}</div>}
        </div>
      </div>
      <div className={`text-right font-medium ${emphasize ? 'text-military-gold' : 'text-slate-800'}`}>{value}</div>
    </div>
  );
}

export default function PropertyDetailPage() {
  const { listingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, locale } = useLanguage();
  const inquiryIdFromUrl = parseInquiryIdFromSearchParams(searchParams);
  const fromAdminListings = isFromAdminListings(searchParams);
  const [listing, setListing] = useState(null);
  const [inquiryContext, setInquiryContext] = useState(null);
  const [inquiryContextStatus, setInquiryContextStatus] = useState('idle');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const [requestingWalkthrough, setRequestingWalkthrough] = useState(false);
  const [samplePreviewEnabled, setSamplePreviewState] = useState(getSamplePreviewEnabled());
  const [baseRegistry, setBaseRegistry] = useState(null);
  const [distanceUnit, setDistanceUnit] = useState(() => getUnitPreference());

  useEffect(() => subscribeToSamplePreviewMode(setSamplePreviewState), []);

  useEffect(() => {
    let cancelled = false;
    basesAPI
      .getAll({ country_code: 'DE' })
      .then((response) => {
        if (!cancelled) setBaseRegistry(response.data);
      })
      .catch((error) => {
        console.error('Failed to load base registry:', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncUnitPreference = () => setDistanceUnit(getUnitPreference());
    syncUnitPreference();
    window.addEventListener('storage', syncUnitPreference);
    window.addEventListener('focus', syncUnitPreference);
    return () => {
      window.removeEventListener('storage', syncUnitPreference);
      window.removeEventListener('focus', syncUnitPreference);
    };
  }, []);

  useEffect(() => {
    loadListing();
  }, [listingId, samplePreviewEnabled]);

  useEffect(() => {
    if (!inquiryIdFromUrl) {
      setInquiryContext(null);
      setInquiryContextStatus('idle');
      return;
    }
    if (!isAuthenticated) {
      setInquiryContext(null);
      setInquiryContextStatus('unavailable');
      return;
    }

    let cancelled = false;
    setInquiryContextStatus('loading');
    api
      .get('/bookings/my-inquiries')
      .then((res) => {
        if (cancelled) return;
        const inquiries = res.data?.inquiries || [];
        const match = resolveInquiryFromList(inquiries, inquiryIdFromUrl, listingId);
        if (match) {
          setInquiryContext(pickSafeInquiryContextForBanner(match));
          setInquiryContextStatus('found');
        } else {
          setInquiryContext(null);
          setInquiryContextStatus('missing');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInquiryContext(null);
          setInquiryContextStatus('missing');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inquiryIdFromUrl, listingId, isAuthenticated]);

  useEffect(() => {
    if (!listing?.id) return;
    trackEvent('property_view', listingAnalyticsProps(listing));
  }, [listing?.id]);

  const applySampleListing = () => {
    const sampleListing = findSampleListingById(listingId);
    if (!sampleListing) {
      setListing(null);
      setReviews([]);
      setLoading(false);
      return;
    }
    setListing(sampleListing);
    setReviews(sampleReviewsByListingId[listingId] || []);
    setLoading(false);
  };

  const loadListing = async () => {
    if (samplePreviewEnabled) {
      applySampleListing();
      return;
    }
    setLoading(true);
    try {
      const [listingRes, reviewsRes] = await Promise.all([
        listingAPI.getById(listingId),
        reviewAPI.getForListing(listingId).catch(() => ({ data: [] })),
      ]);
      setListing(listingRes.data);
      setReviews(Array.isArray(reviewsRes.data) ? reviewsRes.data : []);
    } catch (error) {
      console.error('Failed to load listing:', error);
      setListing(null);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestWalkthrough = async () => {
    if (samplePreviewEnabled) {
      setWalkthroughOpen(false);
      toast.success(
        t(
          'property.toast.walkthrough_preview_success',
          'Sample preview mode: walkthrough request demo completed.',
        ),
      );
      return;
    }
    if (!isAuthenticated) {
      navigate(`/login?redirect=/listings/${listingId}`);
      return;
    }
    setRequestingWalkthrough(true);
    try {
      await walkthroughAPI.create({
        listing_id: listingId,
        preferred_method: 'ZOOM',
        preferred_language: 'en',
      });
      setWalkthroughOpen(false);
      toast.success(
        t(
          'property.toast.walkthrough_submitted',
          'Walkthrough request submitted. The owner will contact you soon.',
        ),
      );
    } catch (error) {
      console.error('Failed to request walkthrough:', error);
      toast.error(
        t(
          'property.toast.walkthrough_failed',
          'Failed to submit walkthrough request. Please try again.',
        ),
      );
    } finally {
      setRequestingWalkthrough(false);
    }
  };

  const handleCreateListingAlert = async () => {
    if (samplePreviewEnabled) {
      toast.success(
        t(
          'property.toast.reminder_preview_success',
          'Sample preview mode: reminder workflow demo completed.',
        ),
      );
      return;
    }
    if (!user) {
      toast.error(
        t('property.toast.reminder_login_required', 'Please login to set reminders'),
      );
      return;
    }
    try {
      await alertAPI.create({
        type: 'listing_reminder',
        listing_id: listing.id,
        channels: ['email'],
        criteria: { listing_title: listing.title, source: 'property_detail' },
      });
      toast.success(
        t(
          'property.toast.reminder_set',
          "Reminder set. We'll email you about updates to this property.",
        ),
      );
    } catch (error) {
      console.error('Failed to create alert:', error);
      toast.error(
        error.response?.data?.detail || t('property.toast.reminder_failed', 'Failed to set reminder'),
      );
    }
  };

  const areaDistanceLine = useMemo(
    () => (listing && baseRegistry ? getListingAreaDistanceLine(listing, baseRegistry, distanceUnit) : null),
    [listing, baseRegistry, distanceUnit],
  );

  const areaIntelBaseTargets = useMemo(() => {
    const lookup = baseRegistry ? buildBaseLookup(baseRegistry) : null;
    return (listing?.nearby_bases || []).map((label) => {
      const base = lookup ? resolveBaseForLabel(label, lookup) : null;
      return base ? { name: base.name, lat: base.lat, lng: base.lng } : { name: label };
    });
  }, [listing, baseRegistry]);

  const nearestBaseMatch = useMemo(
    () => (listing && baseRegistry ? findNearestListedBase(listing, baseRegistry) : null),
    [listing, baseRegistry],
  );

  const nearbyBases = listing?.nearby_bases || [];
  const showBaseProximitySection = Boolean(areaDistanceLine || nearbyBases.length > 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20">
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <h1 className="font-heading text-2xl text-slate-800 mb-4">{t('property.not_found', 'Property Not Found')}</h1>
          <Link to="/listings">
            <Button variant="outline" className="border-slate-300 text-slate-700">{t('property.back_to_listings', 'Back to Listings')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const photos = resolveListingPhotoEntries(listing);
  const hasUploadedPhotos = photos.length > 0;

  const baseRent = getBaseRent(listing);
  const monthlyTotal = getMonthlyTotal(listing);
  const monthlyReferenceAmount = monthlyTotal ?? baseRent;
  const priceLabelKey = getMonthlyPriceLabelKey(listing);
  const availabilityStatusLabels = {
    AVAILABLE: t('listing.status.available', 'AVAILABLE'),
    UNAVAILABLE: t('listing.status.unavailable', 'UNAVAILABLE'),
    RESERVED: t('listing.status.reserved', 'RESERVED'),
    BOOKED: t('listing.status.booked', 'BOOKED'),
    RENTED: t('listing.status.rented', 'RENTED'),
    COMING_SOON: t('listing.coming_soon_badge', 'COMING SOON'),
    DEACTIVATED: t('listing.status.deactivated', 'DEACTIVATED'),
  };
  const photoCount = listing.photo_count ?? listing.photos?.length ?? 0;
  const reviewCount = listing.review_count ?? reviews.length ?? 0;
  const ownerConfirmationLabel = getOwnerConfirmationLabel(listing, t);
  const shareTitle = translateTemplate(
    t('property.share.title', '{title} - TLA Housing Europe'),
    { title: listing.title },
  );
  const shareDescription = hasValue(monthlyReferenceAmount)
    ? translateTemplate(
        t(
          'property.share.description_with_price',
          'Check out this {bedrooms}-bedroom property in {location} from {price} per month',
        ),
        {
          bedrooms: listing.bedrooms,
          location: listing.location_name,
          price: formatCurrency(monthlyReferenceAmount),
        },
      )
    : translateTemplate(
        t(
          'property.share.description_no_price',
          'Check out this {bedrooms}-bedroom property in {location}',
        ),
        { bedrooms: listing.bedrooms, location: listing.location_name },
      );

  return (
    <div className="min-h-screen bg-slate-100 pt-16" data-testid="property-detail-page">
      <div className="relative h-[50vh] md:h-[60vh] bg-white">
        {hasUploadedPhotos ? (
          <img src={photos[currentPhotoIndex]?.url} alt={listing.title} className="w-full h-full object-cover" />
        ) : (
          <ListingPhotoPending className="w-full h-full" />
        )}
        {hasUploadedPhotos && <div className="hero-overlay absolute inset-0" />}

        {photos.length > 1 && (
          <>
            <button onClick={() => setCurrentPhotoIndex((index) => (index - 1 + photos.length) % photos.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-slate-100/80 hover:bg-slate-100 rounded-full flex items-center justify-center text-slate-700">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button onClick={() => setCurrentPhotoIndex((index) => (index + 1) % photos.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-slate-100/80 hover:bg-slate-100 rounded-full flex items-center justify-center text-slate-700">
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        <Link
          to={fromAdminListings ? ADMIN_LISTINGS_RETURN_URL : '/listings'}
          className="absolute top-4 left-4 flex items-center gap-2 px-3 py-2 bg-slate-100/80 hover:bg-slate-100 rounded-sm text-slate-700 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          {fromAdminListings ? t('admin.back_to_admin_listings', 'Back to Admin Listings') : t('property.back', 'Back')}
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {inquiryIdFromUrl && inquiryContextStatus === 'found' && (
          <BookingInquiryContextBanner inquiry={inquiryContext} />
        )}
        {inquiryIdFromUrl && inquiryContextStatus === 'unavailable' && (
          <BookingInquiryContextBanner unavailable />
        )}
        {inquiryIdFromUrl && inquiryContextStatus === 'missing' && (
          <BookingInquiryContextBanner unavailable />
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              {samplePreviewEnabled && (
                <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 bg-signal-blue/10 border border-signal-blue/20 rounded-full text-sm text-signal-blue">
                  {t(
                    'property.sample_preview_banner',
                    'Sample preview mode is active. This detail page is using curated demo listing data for client walkthroughs.',
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {(listing.tla_status === 'eligible' || listing.tla_eligible) && (
                  <Badge className="bg-military-gold/20 text-military-gold border border-military-gold/30"><Shield className="w-3 h-3 mr-1" />{t('listing.tla_eligible_badge', 'TLA ELIGIBLE')}</Badge>
                )}
                {listing.availability_status && (() => {
                  const detailStatusColors = {
                    AVAILABLE: 'bg-emerald-500/20 text-emerald-700 border-emerald-500/30',
                    COMING_SOON: 'bg-military-gold/20 text-military-gold border-military-gold/30',
                    RESERVED: 'bg-amber-500/20 text-amber-700 border-amber-500/30',
                    BOOKED: 'bg-orange-500/20 text-orange-700 border-orange-500/30',
                    RENTED: 'bg-orange-500/20 text-orange-700 border-orange-500/30',
                    UNAVAILABLE: 'bg-slate-500/20 text-slate-700 border-slate-500/30',
                    DEACTIVATED: 'bg-red-500/20 text-red-700 border-red-500/30',
                  };
                  const statusLabel =
                    availabilityStatusLabels[listing.availability_status] ||
                    listing.availability_status.replaceAll('_', ' ');
                  return (
                    <Badge className={`border ${detailStatusColors[listing.availability_status] || 'bg-slate-500/20 text-slate-700 border-slate-500/30'}`}>
                      {statusLabel}
                    </Badge>
                  );
                })()}
                <Badge className="border bg-slate-100 text-slate-700 border-slate-300"><Clock className="w-3 h-3 mr-1" />{ownerConfirmationLabel}</Badge>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 mb-2">{listing.title}</h1>
              <div className="flex flex-wrap items-center gap-3 text-slate-600">
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /><span>{listing.location_name}{listing.region ? `, ${listing.region}` : ''}, {t('locations.germany', 'Germany')}</span></div>
                {hasValue(listing.avg_rating) && reviewCount > 0 && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-700 text-sm">
                    <Star className="w-3.5 h-3.5 text-military-gold fill-military-gold" />
                    {Number(listing.avg_rating).toFixed(1)} ({translateTemplate(t('property.reviews_count_label', '{count} reviews'), { count: reviewCount })})
                  </div>
                )}
                {photoCount > 0 && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-700 text-sm">
                    {translateTemplate(t('property.photos_count_label', '{count} photos'), { count: photoCount })}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div className="text-center"><Bed className="w-6 h-6 text-signal-blue mx-auto mb-2" /><div className="font-heading text-2xl font-bold text-slate-800">{listing.bedrooms}</div><div className="label-tactical">{t('property.bedrooms', 'Bedrooms')}</div></div>
                <div className="text-center"><Bath className="w-6 h-6 text-signal-blue mx-auto mb-2" /><div className="font-heading text-2xl font-bold text-slate-800">{listing.bathrooms}</div><div className="label-tactical">{t('property.bathrooms', 'Bathrooms')}</div></div>
                {listing.sqm && <div className="text-center"><Maximize2 className="w-6 h-6 text-signal-blue mx-auto mb-2" /><div className="font-heading text-2xl font-bold text-slate-800">{listing.sqm}</div><div className="label-tactical">{t('property.sq_meters', 'SQ. Meters')}</div></div>}
                {hasValue(monthlyReferenceAmount) && <div className="text-center"><div className="font-heading text-2xl font-bold text-military-gold">{formatCurrency(monthlyReferenceAmount)}</div><div className="label-tactical">{t(priceLabelKey, hasValue(monthlyTotal) ? 'Confirmed monthly total' : 'Base rent only')}</div></div>}
              </div>
            </div>

            {showBaseProximitySection && (
              <div
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
                data-testid="base-proximity-section"
              >
                <h3 className="font-heading text-sm font-bold text-military-gold mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {t('property.base_key_distances', 'BASE & KEY DISTANCES')}
                </h3>

                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                    {t('property.nearest_base', 'Nearest base')}
                  </p>
                  {areaDistanceLine ? (
                    <>
                      {nearestBaseMatch?.base?.name && (
                        <p className="text-sm font-semibold text-slate-800 mb-1">{nearestBaseMatch.base.name}</p>
                      )}
                      <p className="text-sm font-medium text-slate-800" data-testid="listing-area-distance">
                        {areaDistanceLine}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-600">
                      {t('listing.near', 'Near')} {nearbyBases.join(', ')}
                    </p>
                  )}
                </div>

                <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                  {t('property.area_distance_helper', AREA_DISTANCE_HELPER_COPY)}
                </p>

              </div>
            )}

            <AreaIntelligenceModule
              origin={
                Number.isFinite(listing.location_lat) && Number.isFinite(listing.location_lng)
                  ? { lat: listing.location_lat, lng: listing.location_lng }
                  : null
              }
              baseTargets={areaIntelBaseTargets}
              distanceUnit={distanceUnit}
            />

            <div>
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('property.about_title', 'ABOUT THIS PROPERTY')}</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap mb-6">{listing.description}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                  <h3 className="font-heading text-sm font-bold text-military-gold mb-4 flex items-center gap-2"><Home className="w-4 h-4" />{t('property.object_details', 'OBJECT DETAILS')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between gap-4"><span className="text-slate-500">{t('property.property_type', 'Property Type')}</span><span className="text-slate-800 font-medium text-right">{translateListingEnum(t, 'property_type', listing.property_type)}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-500">{t('property.living_space', 'Living Space')}</span><span className="text-slate-800 font-medium text-right">{listing.sqm ? translateTemplate(t('property.living_space_value', '{sqm} m² ({sqft} sq ft)'), { sqm: listing.sqm, sqft: Math.round(listing.sqm * 10.764) }) : '—'}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-500">{t('property.rooms', 'Rooms')}</span><span className="text-slate-800 font-medium text-right">{hasValue(listing.bedrooms) ? listing.bedrooms + 1 : '—'}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-500">{t('property.bedrooms', 'Bedrooms')}</span><span className="text-slate-800 font-medium text-right">{listing.bedrooms ?? '—'}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-500">{t('property.bathrooms', 'Bathrooms')}</span><span className="text-slate-800 font-medium text-right">{listing.bathrooms ?? '—'}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-500">{t('property.floor', 'Floor')}</span><span className="text-slate-800 font-medium text-right">{listing.floor || '—'}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-500">{t('property.year_built', 'Year Built')}</span><span className="text-slate-800 font-medium text-right">{listing.year_built || '—'}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-500">{t('property.last_renovated', 'Last Renovated')}</span><span className="text-slate-800 font-medium text-right">{listing.last_renovated || '—'}</span></div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                  <h3 className="font-heading text-sm font-bold text-military-gold mb-4 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{t('property.equipment_title', 'EQUIPMENT & FEATURES')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between gap-4"><span className="text-slate-600">{t('property.kitchen', 'Kitchen')}</span><span className="text-slate-800 font-medium text-right">{translateListingEnum(t, 'kitchen_type', listing.kitchen_type)}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-600">{t('property.heating', 'Heating')}</span><span className="text-slate-800 font-medium text-right">{translateListingEnum(t, 'heating_type', listing.heating_type)}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-600">{t('property.energy_rating', 'Energy Rating')}</span><span className="text-slate-800 font-medium text-right">{listing.energy_rating || '—'}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-600">{t('property.feature_parking', 'Parking')}</span><span className="text-slate-800 font-medium text-right">{listing.parking_type ? translateListingEnum(t, 'parking_type', listing.parking_type) : (hasValue(listing.parking_spaces) ? translateTemplate(t('property.parking_spaces_count', '{count} space(s)'), { count: listing.parking_spaces }) : '—')}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-600">{t('property.pets_allowed', 'Pets Allowed')}</span><span className="text-slate-800 font-medium text-right">{listing.pets_allowed === true ? t('common.yes', 'Yes') : listing.pets_allowed === false ? t('common.no', 'No') : (listing.pet_policy ? translateListingEnum(t, 'pet_policy', listing.pet_policy) : t('common.on_request', 'On request'))}</span></div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 mt-6">
                <h3 className="font-heading text-sm font-bold text-military-gold mb-4 flex items-center gap-2"><Calendar className="w-4 h-4" />{t('property.availability_title', 'AVAILABILITY')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex justify-between md:block gap-4"><span className="text-slate-600">{t('property.avail_from', 'Available From')}</span><span className="text-slate-800 font-medium md:block text-right md:text-left">{listing.available_from ? formatDate(listing.available_from, '—', locale) : listing.days_until_available === 0 ? t('property.avail_immediately', 'Immediately') : t('property.check_with_owner', 'Check with owner')}</span></div>
                  <div className="flex justify-between md:block gap-4"><span className="text-slate-600">{t('property.days_until_avail', 'Days Until Available')}</span><span className="text-slate-800 font-medium md:block text-right md:text-left">{hasValue(listing.days_until_available) ? `${listing.days_until_available} ${t('common.days', 'days')}` : t('property.check_with_owner', 'Check with owner')}</span></div>
                  <div className="flex justify-between md:block gap-4"><span className="text-slate-600">{t('property.min_stay', 'Minimum Stay')}</span><span className="text-slate-800 font-medium md:block text-right md:text-left">{hasValue(listing.min_stay_days) ? `${listing.min_stay_days} ${t('common.days', 'days')}` : t('property.to_be_confirmed', 'To be confirmed')}</span></div>
                  <div className="flex justify-between md:block gap-4"><span className="text-slate-600">{t('property.max_stay', 'Maximum Stay')}</span><span className="text-slate-800 font-medium md:block text-right md:text-left">{hasValue(listing.max_stay_days) ? `${listing.max_stay_days} ${t('common.days', 'days')}` : t('property.no_limit', 'No limit provided')}</span></div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-sm font-medium text-slate-700">{ownerConfirmationLabel}</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2"><Euro className="w-5 h-5 text-military-gold" />{t('property.cost_breakdown_title', 'COST BREAKDOWN')}</h2>
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-5">
                  <h3 className="font-heading text-sm font-bold text-slate-600 mb-4">{t('property.monthly_costs', 'MONTHLY COSTS')}</h3>
                  <div className="space-y-0">
                    <CostRow icon={<Home className="w-4 h-4 text-slate-500" />} label={t('property.base_rent', 'Base Rent')} description={t('property.without_utilities', 'Without utilities (Kaltmiete)')} value={formatCurrency(baseRent, '/mo')} />
                    <CostRow icon={<Zap className="w-4 h-4 text-slate-500" />} label={t('property.utilities', 'Utilities')} description={t('property.utilities_desc', 'Heating, water, waste (Nebenkosten)')} value={hasValue(listing.utilities_cost) ? formatCurrency(listing.utilities_cost, '/mo') : t('property.not_confirmed', 'Not confirmed')} />
                    <CostRow icon={<Zap className="w-4 h-4 text-yellow-500" />} label={t('property.electricity', 'Electricity')} description={listing.electricity_included ? t('property.included_in_rent', 'Included in rent') : t('property.electricity_owner_provided', 'Shown only when the owner provided it')} value={listing.electricity_included ? t('property.included', 'Included') : hasValue(listing.electricity_cost) ? formatCurrency(listing.electricity_cost, '/mo') : t('property.not_confirmed', 'Not confirmed')} />
                    <CostRow icon={<Wifi className="w-4 h-4 text-signal-blue" />} label={t('property.internet', 'Internet & TV')} description={listing.internet_speed || t('property.high_speed_wifi', 'High-speed WiFi')} value={listing.internet_included ? t('property.included', 'Included') : hasValue(listing.internet_cost) ? formatCurrency(listing.internet_cost, '/mo') : t('property.not_confirmed', 'Not confirmed')} />
                    {(listing.parking_included || hasValue(listing.parking_cost) || listing.parking_type) && <CostRow icon={<ParkingCircle className="w-4 h-4 text-slate-500" />} label={t('property.parking', 'Parking')} description={listing.parking_type ? translateListingEnum(t, 'parking_type', listing.parking_type) : t('property.parking_details_owner', 'Parking details from owner')} value={listing.parking_included ? t('property.included', 'Included') : hasValue(listing.parking_cost) ? formatCurrency(listing.parking_cost, '/mo') : t('property.not_confirmed', 'Not confirmed')} />}
                    {hasValue(listing.pet_fee) && <CostRow icon={<span className="text-base">🐾</span>} label={t('property.pet_fee', 'Pet Fee')} description={t('property.pet_fee_owner_desc', 'Only shown when explicitly provided by the owner')} value={formatCurrency(listing.pet_fee, '/mo')} />}
                    {hasValue(listing.other_costs) && <CostRow icon={<span className="w-4 h-4 text-slate-500 flex items-center justify-center">+</span>} label={t('property.other_costs', 'Other Costs')} description={t('property.other_costs_owner_desc', 'Owner-provided additional monthly cost')} value={formatCurrency(listing.other_costs, '/mo')} />}
                    <CostRow icon={<Euro className="w-4 h-4 text-military-gold" />} label={t('property.total_monthly', 'Total Monthly')} description={hasValue(monthlyTotal) ? t('property.confirmed_monthly_backend_desc', 'Confirmed monthly total from backend listing data') : t('property.owner_not_confirmed_total_desc', 'Owner has not confirmed a full monthly total yet')} value={hasValue(monthlyTotal) ? formatCurrency(monthlyTotal, '/mo') : t('property.not_confirmed', 'Not confirmed')} emphasize />
                  </div>
                </div>

                <div className="p-5 bg-slate-50 border-t border-slate-200">
                  <h3 className="font-heading text-sm font-bold text-slate-600 mb-4">{t('property.one_time_costs', 'ONE-TIME COSTS')}</h3>
                  <div className="space-y-0">
                    {(hasValue(listing.security_deposit) || hasValue(listing.deposit_months)) && <CostRow icon={<Shield className="w-4 h-4 text-slate-500" />} label={t('property.security_deposit', 'Security Deposit')} description={hasValue(listing.deposit_months) ? translateTemplate(t('property.deposit_months_noted', '{months} month(s) rent noted by owner'), { months: listing.deposit_months }) : t('property.refundable_deposit_desc', 'Refundable deposit if required by the owner')} value={hasValue(listing.security_deposit) ? formatCurrency(listing.security_deposit) : t('property.not_confirmed', 'Not confirmed')} />}
                    {hasValue(listing.cleaning_fee) && <CostRow icon={<Trash2 className="w-4 h-4 text-slate-500" />} label={t('property.cleaning_fee', 'Final Cleaning Fee')} description={t('property.cleaning_fee_owner_desc', 'Only shown when explicitly provided by the owner')} value={formatCurrency(listing.cleaning_fee)} />}
                  </div>
                  <div className="mt-4 p-4 rounded-lg bg-white border border-slate-200">
                    <div className="flex items-start gap-2"><Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" /><div className="text-sm text-slate-600"><p className="font-medium text-slate-700 mb-1">{t('property.move_in_payment_timing', 'Move-in and payment timing are confirmed during inquiry.')}</p><p>{t('property.no_browser_estimates', 'This page no longer estimates booking deposits, utility bundles, or move-in totals in the browser. Final numbers should come from the owner, the agreement, and the platform flow.')}</p></div></div>
                  </div>
                </div>
              </div>
            </div>

            {listing.amenities?.length > 0 && <div><h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('property.amenities_title', 'AMENITIES')}</h2><div className="flex flex-wrap gap-2">{listing.amenities.map((amenity, index) => <span key={index} className="px-3 py-1.5 bg-slate-800 text-slate-100 text-sm rounded-sm">{amenity}</span>)}</div></div>}
            {listing.virtual_tour_url && <div><h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('property.virtual_tour_title', 'VIRTUAL TOUR')}</h2><a href={listing.virtual_tour_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-3 bg-signal-blue/10 border border-signal-blue/30 text-signal-blue rounded-sm hover:bg-signal-blue/20"><View className="w-5 h-5" />{t('property.view_tour_cta', 'View 360° Tour')}<ExternalLink className="w-4 h-4" /></a></div>}
            <div><h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('property.reviews_title', 'REVIEWS')}</h2>{reviews.length > 0 ? <div className="space-y-4">{reviews.map((review) => <div key={review.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4"><div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center"><User className="w-4 h-4 text-slate-200" /></div><div><p className="text-sm text-slate-700">{review.user_name || t('property.anonymous_reviewer', 'Anonymous')}</p><div className="flex items-center gap-1">{Array.from({ length: 5 }).map((_, index) => <Star key={index} className={`w-3 h-3 ${index < review.rating ? 'text-military-gold fill-military-gold' : 'text-slate-300'}`} />)}</div></div></div><p className="text-slate-600 text-sm">{review.review_text}</p></div>)}</div> : <p className="text-slate-500">{t('property.no_reviews_yet', 'No reviews yet for this property.')}</p>}</div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sticky top-24">
              {hasValue(monthlyReferenceAmount) && (
                <div className="text-center mb-6 pb-6 border-b border-slate-200">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t(priceLabelKey, hasValue(monthlyTotal) ? 'Confirmed monthly total' : 'Base rent only')}</div>
                  <div className="font-heading text-4xl font-bold text-military-gold">{formatCurrency(monthlyReferenceAmount)}</div>
                  <p className="text-slate-600 text-sm">{hasValue(monthlyTotal) ? t('property.per_month', 'per month') : t('property.base_rent_per_month', 'base rent per month')}</p>
                  <div className="mt-3 pt-3 border-t border-slate-200/60 text-xs text-slate-500 space-y-1">
                    <div className="flex justify-between gap-4"><span>{t('property.base_rent_sidebar', 'Base rent')}</span><span>{formatCurrency(baseRent)}</span></div>
                    <div className="flex justify-between gap-4"><span>{t('property.confirmed_monthly_sidebar', 'Confirmed monthly total')}</span><span>{hasValue(monthlyTotal) ? formatCurrency(monthlyTotal) : t('property.not_confirmed', 'Not confirmed')}</span></div>
                  </div>
                </div>
              )}
              <div className="flex gap-2 mb-6"><FavoriteButton listingId={listing.id} listingTitle={listing.title} listing={listing} className="flex-1" /><ShareButton url={window.location.href} title={shareTitle} description={shareDescription} /></div>
              <div className="mb-6"><BookingCalendar listingId={listing.id} listingTitle={listing.title} listingOwnerId={listing.owner_id} monthlyAmount={monthlyReferenceAmount} /></div>
              {listing.availability_status === 'RESERVED' && (
                <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm font-semibold text-amber-800">{t('property.currently_reserved', 'Currently reserved')}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    {listing.available_from
                      ? translateTemplate(
                          t('property.inquiries_accepted_from', 'Inquiries accepted now — unit available from {date}.'),
                          { date: formatDate(listing.available_from, '—', locale) },
                        )
                      : t('property.reserved_contact_owner', 'This unit is reserved. Contact the owner to confirm timing.')}
                  </p>
                </div>
              )}
              <div className="space-y-3">
                {listing.live_walkthrough_enabled && <Dialog open={walkthroughOpen} onOpenChange={setWalkthroughOpen}><DialogTrigger asChild><Button className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white rounded-sm" data-testid="request-walkthrough-btn"><Video className="w-4 h-4 mr-2" />{t('property.request_walkthrough', 'Request Live Walkthrough')}</Button></DialogTrigger><DialogContent className="bg-white border-slate-300"><DialogHeader><DialogTitle className="text-slate-800">{t('property.request_walkthrough', 'Request Live Walkthrough')}</DialogTitle></DialogHeader><div className="space-y-4 mt-4"><p className="text-slate-600 text-sm">{t('property.walkthrough_desc', 'The property owner will receive your request and propose available times for a live video walkthrough.')}</p><Button onClick={handleRequestWalkthrough} disabled={requestingWalkthrough} className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white">{requestingWalkthrough ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.submitting', 'Submitting...')}</> : t('property.confirm_request', 'Confirm Request')}</Button></div></DialogContent></Dialog>}
                <Button variant="outline" className="w-full border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="notify-btn" onClick={handleCreateListingAlert}><Clock className="w-4 h-4 mr-2" />{t('property.remind_later', 'Remind Me Later')}</Button>
              </div>
              {listing.owner_name && <div className="mt-6 pt-6 border-t border-slate-200"><p className="label-tactical mb-2">{t('property.listed_by', 'Listed By')}</p><div className="flex items-center gap-3"><div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-slate-200" /></div><div><p className="text-slate-800 font-medium">{listing.owner_name}</p><p className="text-xs text-slate-500">{t('property.owner_label', 'Property Owner')}</p></div></div></div>}
              {listing.pet_policy && <div className="mt-6 pt-6 border-t border-slate-200"><p className="label-tactical mb-2">{t('property.pet_policy_label', 'Pet Policy')}</p><p className="text-slate-700 text-sm">{listing.pet_policy}</p></div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
